import { Style } from "@react-pdf/types";
import { AutoCompleteEnum, ButtonLayout, ExcludeCharacterFilterTypes, IPopupHint } from "@shared/common";
import * as React from "react";
import { IArrayValidation } from "../Validations/IArrayValidation";
import { FormControlDependsOnSchema } from "./DependsOn";
import { IValidation } from "../Validations/IValidation";
import { Nullable } from "@core/utils";

export type FilterFieldSchema =
  | TextFieldSchema
  | TextAreaFieldSchema
  | DateFieldSchema
  | SelectFieldSchema
  | RadioBoxFieldSchema
  | CheckboxGroupFieldSchema;

export type FieldSchema =
  | TextFieldSchema
  | TextAreaFieldSchema
  | AmountFieldSchema
  | DateFieldSchema
  | ArrayFieldSchema
  | ObjectFieldSchema
  | SelectFieldSchema
  | RadioBoxFieldSchema
  | FileFieldSchema
  | FileManagementFieldSchema
  | CheckboxGroupFieldSchema
  | ReadOnlyFieldSchema
  | HiddenFieldSchema
  | OtherFieldSchema
  | DynamicFieldSchema;

export interface FormControlOnChangeSchema {
  timeoutInMilliseconds: number;
  afterChangeGetValuesToBeSet: (
    basePath: string,
    value: any
  ) => Promise<{ key: string; value: string | undefined | { [key: string]: string | undefined } }[]>;
  triggerValidationAfterSetValues: boolean;
  clearOnError?: boolean;
}

export type FormFieldSchema = Exclude<FieldSchema, ArrayFieldSchema | ObjectFieldSchema | DynamicFieldSchema>;

export interface FormControlSchema {
  label?: string;
  subLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  validate?: IValidation[] | (() => IValidation[]);
  onChange?: FormControlOnChangeSchema;
  dependsOn?: FormControlDependsOnSchema[];
  ui?: FormControlUiSchema;
  infoText?: string;
  popupHint?: IPopupHint;
  icon?: "search" | string;
  beforeFieldContent?: JSX.Element;
  afterFieldContent?: JSX.Element;
  showIsValidCheckmark?: boolean;
}

export enum FormType {
  "Read",
  "Write"
}

type FlexDirectionType = "row" | "row-reverse" | "column" | "column-reverse";
type FormatValueFunctionType = (value: string | undefined | null) => string;

type RenderFunctionType = (value: string | undefined | null) => React.ReactNode;

export enum CollapseMode {
  Toggle = "Toggle",
  Target = "Target"
}

export interface CollapseSettings {
  groupId: string;
  mode: CollapseMode;
}

export interface ItemValuePdfUISettings {
  style?: Style;
  defaultValue?: string;
}

export interface FormControlUiSchema {
  className?: string;
  flexDirection?: FlexDirectionType;
  hideHeaderText?: boolean;
  formTabel?: {
    customLabel?: React.ReactNode;
  };
  readOverview?: {
    render?: boolean;
    isNumberedListItem?: boolean;
    hideWhenAllPropertiesAreNotGiven?: boolean;
    isFilesOverview?: boolean;
    flexDirection?: FlexDirectionType;
    collapse?: CollapseSettings;
    hideWhenEmpty?: boolean;
  };
  formOverview?: {
    render?: boolean;
    renderFormGroupWhenHidden?: boolean;
    flexDirection?: FlexDirectionType;
    formGroupCustomCssClass?: string;
  };
  pdfOverview?: {
    itemContainerStyle?: Style;
    label?: {
      style?: Style;
      show?: boolean;
      casing?: "uppercase" | "default";
    };
    itemValue?: ItemValuePdfUISettings;
    section?: {
      canWrap?: boolean;
      startOnNewPage?: boolean;
    };
  };
  filterOverview?: {
    lineBreak?: boolean;
  };
  theme?: "light" | "dark";
  isNumberedList?: boolean;
  showTitle?: boolean;
  inputDisplayStyle?: "inline" | "block";
  formGroupfullWidth?: boolean;
}

export interface ReadOnlyFieldSchema extends FormControlSchema {
  type: "readOnly";
  formatValue?: FormatValueFunctionType;
  render?: RenderFunctionType;
  datasource?: {
    key: string;
    datasourceParams1?: string;
    datasourceParams2?: string;
  };
  options?: SelectFieldOption[];
}

interface BaseFileFieldSchema {
  multiple?: boolean;
  buttonText: string;
  acceptFileTypes?: string;
  fileIndicatorLabel?: string;
  showFileIndicatorLabel?: boolean;
  onDownload?: (resourceId: number) => void;
}

export interface FileFieldSchema extends FormControlSchema, BaseFileFieldSchema {
  type: "file";
  maxFiles?: number;
  showValidationMessageBelowFile?: boolean;
}

export interface FileManagementFieldSchema extends FormControlSchema, BaseFileFieldSchema {
  type: "file-management";
  dropFilesText: string;
}

export interface TextAreaFieldSchema extends FormControlSchema {
  type: "textArea";
}

export interface TextFieldSchema extends FormControlSchema {
  type: "text";
  placeholder?: string;
  excludeCharactersFilter?: ExcludeCharacterFilterTypes;
  overview?: {
    formatValue?: FormatValueFunctionType;
    render?: RenderFunctionType;
  };
  autoComplete?: AutoCompleteEnum;
  maxLengthReachedMessage?: string;
  dontShowMaxLengthReachedMessage?: boolean;
  isPasswordField?: boolean;
}

export interface HiddenFieldSchema extends FormControlSchema {
  type: "hidden";
}

export interface AmountFieldSchema extends FormControlSchema {
  type: "amount";
  placeholder?: string;
  overview?: {
    formatValue?: FormatValueFunctionType;
    render?: RenderFunctionType;
    valueWhenNotGiven?: number;
    beforeText?: {
      text?: string;
      valueOfProperty?: string;
      isAbsolutePath?: boolean;
    };
  };
  decimalScale?: number;
}

export interface DateFieldSchema extends FormControlSchema {
  type: "date";
  placeholder?: string;
  dateMask?: string;
  overview?: {
    formatValue?: FormatValueFunctionType;
    render?: RenderFunctionType;
  };
}

export interface SelectFieldSchema extends FormControlSchema {
  type: "select";
  datasource?: {
    key: string;
    datasourceParams1?: string;
    datasourceParams2?: string;
  };
  options?: SelectFieldOption[];
  formatSelectedLabelValue?: (label: string) => string | undefined;
  optionToContentAfterText?: Map<string, string>;
  multiSelect?: boolean;
  showMultiSelectValueCommaSeparated?: boolean;
  isClearable?: boolean;
  labelFieldName?: string;
  overview?: {
    formatValue?: FormatValueFunctionType;
    render?: RenderFunctionType;
  };
}

export interface SelectFieldOption {
  value: string;
  label: string;
}

export interface CheckboxGroupFieldSchema extends FormControlSchema {
  type: "checkbox-group";
  options: CheckboxFieldOption[];
}

export interface CheckboxFieldOption {
  value: string;
  label: string;
  id: string;
}

export interface OtherFieldSchema extends FormControlSchema {
  type: string;
}

export interface RadioBoxFieldSchema extends FormControlSchema {
  type: "radio";
  addWrapperDivWithClass?: string;
  options?: RadioFieldOption[];
  overview?: {
    formatValue?: FormatValueFunctionType;
    render?: RenderFunctionType;
  };
}

export interface RadioFieldOption {
  value: string;
  label: string;
  id: string;
  explanation?: JSX.Element;
  popupHint?: IPopupHint;
}

export interface ArrayFieldSchema
  extends Pick<
    FormControlSchema,
    | "label"
    | "subLabel"
    | "dependsOn"
    | "ui"
    | "popupHint"
    | "onChange"
    | "afterFieldContent"
    | "beforeFieldContent"
  > {
  type: "array";
  items: FieldSchema;
  id?: string;
  addLabel: string;
  hideToevoegenInAddLabel?: boolean;
  hideAddButton?: boolean;
  validate?: IArrayValidation[];
  maximumNumberOfRows?: number;
  buttonLayout?: ButtonLayout;
  /**
   * @description Option to remove an item from the array with the close-icon
   */
  withClosing?: boolean;
  /**
   * @description Buttons to add on the bottom (default) right of the item
   */
  buttonsBelowItem?: ArrayFieldButton[];
  /**
   * @description Alignment of the buttons below the item
   */
  alignmentButtonsBelowItem?: "left" | "center" | "right";
}

export interface ArrayFieldButton {
  text: string;
  id?: string;
  disabled?: boolean;
  type?: "link" | "button" | "submit" | "reset";
  handle: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    context: { values: Record<string, any>; setUpdate: (values: Record<string, any> | null) => void }
  ) => void;
}

export interface ObjectFieldSchema
  extends Pick<
    FormControlSchema,
    | "label"
    | "subLabel"
    | "dependsOn"
    | "ui"
    | "popupHint"
    | "onChange"
    | "afterFieldContent"
    | "beforeFieldContent"
  > {
  type: "object";
  properties: Record<string, FieldSchema>;
  id?: string;
  className?: string;
  overview?: {
    render?: RenderFunctionType;
  };
}

/**
 * Interface representing a dynamic field schema that extends the FormControlSchema.
 * This schema is used to define dynamic fields within a form.
 *
 * @interface DynamicFieldSchema
 * @extends {FormControlSchema} - BE AWARE: all properties of the FormControlSchema will be IGNORED!
 *
 * @property {string} type - The type of the field, which is always "dynamic".
 * @property {string | string[]} [watch] - Optional field name or field names to watch for changes.
 * @property {<V extends Record<string, any>>(values: V) => Nullable<FieldSchema>} [getSchema] -
 *           Optional function to get the schema based on the provided values.
 * @property {<V extends Record<string, any>, S extends FieldSchema>(values: V, schema?: Nullable<S>) => React.ReactNode} createComponent -
 *           Function to create a React component based on the provided values and optional schema.
 */
export interface DynamicFieldSchema extends FormControlSchema {
  type: "dynamic";
  watch?: string | string[];
  getSchema?: <V extends Record<string, any>>(values: V) => Nullable<FieldSchema>;
  createComponent: <V extends Record<string, any>, S extends FieldSchema>(
    values: V,
    schema?: Nullable<S>
  ) => React.ReactNode;
}
